/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';
import { Button, Flex, Radio, RadioChangeEvent } from 'antd';
import Modal from 'Components/Modal';
import Text from 'Components/Text';
import ModalFooter from 'Components/ModalFooter';
import WarningModal from 'Components/Modal/WarningModal';

import useSwitchClinicState from './useSwitchClinicState';
import styles from './SwitchClinicModal.less';

const CANNOT_SWITCH_CLINIC_ERROR_CODE = 'CANNOT_SWITCH_CLINIC';

interface ISwitchClinicModalProps {
  clinics: { id: number; name: string }[];
  onSwitchClinic: (toClinicId: number) => Promise<void>;
}

const SwitchClinicModal = NiceModal.create<ISwitchClinicModalProps>(
  ({ clinics, onSwitchClinic }) => {
    const modal = useModal();
    const warningModal = useModal(WarningModal);
    const [selectedClinic, setSelectedClinic] = useState<number | null>(clinics[0]?.id || null);
    const { isLoading, error, resetState } = useSwitchClinicState();

    const displayError = () => {
      if (error?.data?.errorCode === CANNOT_SWITCH_CLINIC_ERROR_CODE) return null;
      return error;
    };

    const handleChange = (e: RadioChangeEvent) => {
      setSelectedClinic(e.target.value);
    };

    const handleClose = () => {
      resetState();
      modal.hide();
    };

    const handleConfirm = async () => {
      try {
        await onSwitchClinic(selectedClinic as number);
      } catch (e: any) {
        if (e?.data?.errorCode === CANNOT_SWITCH_CLINIC_ERROR_CODE) {
          warningModal.show({
            title: 'Cannot switch clinic',
            message: e?.data?.message,
          });
        }
      }
    };

    return (
      <Modal {...antdModalV5(modal)} title="Switch Clinic" width={700} className={styles.modal}>
        <Flex vertical gap={16} className="mb-4">
          <Text>
            Your current clinic doesn't support prescription fulfillment. You can switch to other
            clinic to proceed.
          </Text>

          <div className={styles.warning}>
            <Text>Switching clinic will void all existing documents in the current session.</Text>
          </div>

          <Radio.Group onChange={handleChange} value={selectedClinic}>
            <Flex vertical gap={16}>
              {clinics.map(clinic => (
                <Radio key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </Radio>
              ))}
            </Flex>
          </Radio.Group>
        </Flex>

        <ModalFooter error={displayError()}>
          <Flex gap={16}>
            <Button onClick={handleClose}>Close</Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              disabled={!selectedClinic}
              loading={isLoading}>
              Switch Clinic
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
    );
  },
);

export default SwitchClinicModal;
